import { getItemByLabelFactory, getItemByIdFactory } from './_common'
import {
  hasSpecficSelectItemValue,
  isNullFieldValue,
} from '~/services/entityInputFieldsService/getStateForDisabled'
import type { ISelectItem, IEntityMaskData } from '~/types/models'
import type { IContract } from '~/types/contract/models'

export enum ContractTypeIds {
  MSA = 1,
  SA = 2,
  NON_CONTRACT = 3,
}

export function getContractTypes(): ISelectItem[] {
  return [
    { id: ContractTypeIds.SA, label: 'SA' },
    { id: ContractTypeIds.MSA, label: 'MSA' },
    { id: ContractTypeIds.NON_CONTRACT, label: 'Non-Contract' },
  ]
}

export const getContractTypeByLabel = getItemByLabelFactory(getContractTypes)
export const getContractTypeById = getItemByIdFactory(getContractTypes)

/**
 * Just the slice of a contract these predicates actually read.
 *
 * Narrowed rather than taking the full `IContract` so a caller holding a
 * select-list row or an `IEntityDataItem` — which is most of the
 * entityInputFieldsService — can pass it without a cast, and so specs can build
 * minimal objects. Same approach as
 * cynqueModules/componentHelpers/contractsCard/contractRowPredicates.ts.
 */
interface IContractTypeBearer {
  contractType?: ISelectItem | null
  // ⚠ Load-bearing. Without it this is a "weak type" — every member optional —
  // and TypeScript then requires a source to declare at least one overlapping
  // property. `IEntityDataItem` declares none of them (it reaches `contractType`
  // through its own index signature), so every entityInputFieldsService caller
  // would fail to compile. Widening here is what lets those call sites pass an
  // item without a cast.
  [propName: string]: any
}

type TContractTypeBearer = IContractTypeBearer | null | undefined

export function hasMSAContractType(contract: TContractTypeBearer): boolean {
  return contract?.contractType?.id === ContractTypeIds.MSA
}

export function hasSAContractType(contract: TContractTypeBearer): boolean {
  return contract?.contractType?.id === ContractTypeIds.SA
}

export function hasNonContractContractType(
  contract: TContractTypeBearer
): boolean {
  return contract?.contractType?.id === ContractTypeIds.NON_CONTRACT
}

/**
 * SA or Non-Contract — i.e. every contract type an msService may be assigned
 * to.
 *
 * `documentation/rules/contract`: "You cannot assign a contract of type MSA to
 * an msservice." The assignable set is therefore the complement of MSA, which
 * is what this returns. Prefer it over a hand-rolled `=== ContractTypeIds.SA`
 * in any contract picker: that spelling silently drops Non-Contract, which is
 * exactly the defect this predicate exists to prevent.
 */
export function hasSAOrNonContractContractType(
  contract: TContractTypeBearer
): boolean {
  return hasSAContractType(contract) || hasNonContractContractType(contract)
}

//
// Contract-type tests over a LIVE FORM's field list
//
// The four below read the in-progress value of a form's `contractType` field,
// where the four above test a saved IContract. Reach for those when you have an
// entity, these when you have `inputFields`.
//
// They lived in triplicate, byte-identical, in
// contractFor{Company,Opportunity,Order}EntityInputFieldsCommon.ts, and
// consumers imported whichever copy they happened to find — SiloOverview.ts
// pulled isMSAContractType out of the COMPANY file no matter which silo it was
// rendering. Three copies of a domain rule is three places for it to drift.
//
// ⚠ Each returns FALSE when `contractType` has no value yet, because
// isNullFieldValue short-circuits first. On a fresh form that makes every one of
// them false, including the two that look like opposites — which is why the
// MSA-or-Non-Contract one is named for the types it MATCHES rather than as
// "not SA". Under a negated name, false-when-unset reads like a bug; named for
// its members it is plainly right, because an unset type is neither of them.
//

/**
 * True for MSA and for Non-Contract.
 *
 * ⚠ Note which two it pairs. Everything else in the codebase groups
 * Non-Contract with SA — `hasSAOrNonContractContractType` directly above does,
 * and `documentation/rules/contract` scopes the msService-assignment
 * restriction to MSA alone. This is the one predicate that puts Non-Contract
 * with MSA instead, so the two names sit deliberately asymmetrically:
 * `hasSAOrNonContract...` for entities, `isMSAOrNonContract...` for forms.
 *
 * All fourteen callers read it as "the SA-only field does not apply here": the
 * twelve review / submit / accept lifecycle fields in the Order contract form,
 * and the `parentContract` picker in contract.ts and contractFromOpportunity.ts
 * (only an SA hangs off an MSA — `documentation/rules/contract`). That reading
 * is coherent, but it has not been ratified — whether Non-Contract genuinely
 * belongs with MSA here is an open question in the SA / Non-Contract audit. If
 * it turns out it does not, this predicate collapses into isMSAContractType.
 */
export function isMSAOrNonContractContractType(inputFields: IEntityMaskData[]) {
  return (
    !isNullFieldValue({
      inputFields,
      sourceFieldName: 'contractType',
    }) &&
    !hasSpecficSelectItemValue({
      inputFields,
      sourceFieldName: 'contractType',
      id: ContractTypeIds.SA,
    })
  )
}

export function isMSAContractType(inputFields: IEntityMaskData[]) {
  return (
    !isNullFieldValue({
      inputFields,
      sourceFieldName: 'contractType',
    }) &&
    hasSpecficSelectItemValue({
      inputFields,
      sourceFieldName: 'contractType',
      id: ContractTypeIds.MSA,
    })
  )
}

export function isSAContractType(inputFields: IEntityMaskData[]) {
  return (
    !isNullFieldValue({
      inputFields,
      sourceFieldName: 'contractType',
    }) &&
    hasSpecficSelectItemValue({
      inputFields,
      sourceFieldName: 'contractType',
      id: ContractTypeIds.SA,
    })
  )
}

export function isNonContractContractType(inputFields: IEntityMaskData[]) {
  return (
    !isNullFieldValue({
      inputFields,
      sourceFieldName: 'contractType',
    }) &&
    hasSpecficSelectItemValue({
      inputFields,
      sourceFieldName: 'contractType',
      id: ContractTypeIds.NON_CONTRACT,
    })
  )
}
